import {
  Button,
  cellStyles4,
  Chip,
  Columns,
  CustomerService,
  MenuDots,
  Nurse,
  Pharmacist,
  RadioButton,
  StarRating,
  Trash,
  TypableDropdown,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React from 'react';
import {
  dropDownMenuItemsAlphabets,
  dropDownMenuItemsValues,
} from './tableSortHelper';
import { Avatar, Grid, Tooltip } from '@mui/material';
import { getTextWithToolTip } from './riskPatientDetailsHelper';
import {
  decryptData,
  getPriorityButton,
  getRatingColor,
  getRatingText,
  numberToCurrencyFormatter,
} from './helper';
import moment from 'moment';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { formatScreensArray } from '../formatters/UserManagementPageFormatter';

type MetLevel = 'Fully Met' | 'Not Met';

export const getChipComponent = (
  chipType: MetLevel,
  label?: string | React.JSX.Element,
) => {
  switch (chipType) {
    case 'Fully Met':
      return (
        <Chip
          label={
            <Typography
              fontSize="12px"
              lineHeight="15px"
              fontType="medium"
              textColor="#17A39C"
            >
              {label ?? 'Fully Met'}
            </Typography>
          }
          backgroundColor="#08C7B41A"
          border="0.3px solid #17A39C"
          borderRadius="4px"
          padding="5px 8px"
          height="25px"
          width={label ? '120px' : '150px'}
        />
      );
    case 'Not Met':
      return (
        <Chip
          label={
            <Typography
              fontSize="12px"
              lineHeight="15px"
              fontType="medium"
              textColor="#D46A6D"
            >
              {label ?? 'Not Met'}
            </Typography>
          }
          backgroundColor="#FBEEEE"
          border="0.3px solid #D46A6D"
          borderRadius="4px"
          padding="5px 8px"
          height="25px"
          width={label ? '120px' : '150px'}
        />
      );
    default:
      return (
        <Chip
          label={
            <Typography
              fontSize="12px"
              lineHeight="15px"
              fontType="medium"
              textColor={patientAccessTheme.palette.grey[900]}
            >
              {label ?? 'NA'}
            </Typography>
          }
          backgroundColor={patientAccessTheme.palette.grey[300]}
          border={'0.3px solid' + patientAccessTheme.palette.grey[900]}
          borderRadius="4px"
          padding="5px 8px"
          height="25px"
          width={label ? '120px' : '150px'}
        />
      );
  }
};

export interface CareNavigatorType {
  id: string;
  title: string;
  stats: any[];
  careNavigators: any[];
  cases: number;
  members: number;
  moi: number;
}

export const formatCareNavigatorData = (data: any) => {
  const groups: CareNavigatorType[] = data?.map((item: any) => {
    return {
      id: item['_id'],
      title: item?.group_name,
      stats: [
        { label: 'No. of Cases', value: item.cases },
        { label: 'Employees', value: item.employees },
        { label: 'M.O.I', value: item.moi },
      ],
      careNavigators: item.careNavigators,
      cases: item.cases,
      members: item.employees,
      moi: item.moi,
      eg_nid: item.eg_nid,
    };
  });

  return groups;
};

export const individualGroupsTableColumns: Columns[] = [
  {
    name: 'Group Name',
    sortColumnName: 'group_name',
    align: 'left',
    cellStyles: cellStyles4,
    width: '190px',
    showEllipsis: true,
    ellipsisWidth: '170px',
    showThisColumn: true,
    DropDownMenuItems: dropDownMenuItemsAlphabets,
  },
  {
    name: 'Care Navigators',
    align: 'left',
    cellStyles: cellStyles4,
    width: '200px',
  },
  {
    name: 'Members',
    align: 'left',
    cellStyles: cellStyles4,
    width: '175px',
    sortColumnName: 'employees',
    showThisColumn: true,
    DropDownMenuItems: dropDownMenuItemsValues,
  },
  {
    name: 'M.O.I',
    align: 'left',
    cellStyles: cellStyles4,
    width: '150px',
    sortColumnName: 'moi',
    showThisColumn: true,
    DropDownMenuItems: dropDownMenuItemsValues,
  },
  {
    name: 'Open Cases',
    align: 'left',
    cellStyles: cellStyles4,
    width: '200px',
    sortColumnName: 'cases',
    showThisColumn: true,
    DropDownMenuItems: dropDownMenuItemsValues,
  },
  {
    name: '',
    align: 'left',
    cellStyles: cellStyles4,
    width: '130px',
  },
];

const formatMemberForTable = (
  name: string,
  _id: string,
  handleMemberClick: any,
  isToolTip: boolean = false,
) => {
  return (
    <Grid container direction="row">
      <Grid>
        <Button
          width="100%"
          variant="text"
          justifyContent="left"
          styleProps={{ padding: '6px 0px' }}
          onClick={() => handleMemberClick(_id)}
          label={
            <Typography
              style={{
                fontSize: '14px',
                lineHeight: '28px',
                color: patientAccessTheme.palette.primary.main,
                fontWeight: 500,
                margin: '0px 0px 0px 0px',
                background: `linear-gradient(to right, ${patientAccessTheme.palette.primary[500]} 50%, rgba(255, 255, 255, 0) 0%)`,
                backgroundPosition: 'bottom',
                backgroundRepeat: 'repeat-x',
                backgroundSize: '6px 1px',
              }}
            >
              {isToolTip
                ? name.length > 12
                  ? getTextWithToolTip(name, 12)
                  : name
                : name}
            </Typography>
          }
        />
      </Grid>
    </Grid>
  );
};

const formatEmployerGroupNameForTable = (
  name: string,
  _id: string,
  handleEmployeerGroupCardClick: any,
  isToolTip: boolean = false,
  data: any,
  isCaseClosed?: boolean,
) => {
  return (
    <Grid container direction="row">
      <Grid>
        <Button
          width="100%"
          variant="text"
          justifyContent="left"
          styleProps={{ padding: '6px 0px' }}
          onClick={() =>
            handleEmployeerGroupCardClick({ ...data, isCaseClosed })
          }
          label={
            <Typography
              style={{
                fontSize: '14px',
                lineHeight: '28px',
                color: patientAccessTheme.palette.primary.main,
                fontWeight: 500,
                margin: '0px 0px 0px 0px',
                background: `linear-gradient(to right, ${patientAccessTheme.palette.primary[500]} 50%, rgba(255, 255, 255, 0) 0%)`,
                backgroundPosition: 'bottom',
                backgroundRepeat: 'repeat-x',
                backgroundSize: '6px 1px',
              }}
            >
              {isToolTip
                ? name.length > 15
                  ? getTextWithToolTip(name, 15)
                  : name
                : name}
            </Typography>
          }
        />
      </Grid>
    </Grid>
  );
};

const formatCareNavigatorForTable = (
  name: string,
  _id: string,
  role: string,
  handleCareNavClick: any,
) => {
  return (
    <Grid container direction="row">
      <Grid>
        <Button
          width="100%"
          variant="text"
          justifyContent="left"
          styleProps={{ padding: '6px 0px' }}
          onClick={() => handleCareNavClick(_id)}
          label={
            <Grid
              container
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '4px',
                alignItems: 'center',
                background: `linear-gradient(to right, ${patientAccessTheme.palette.primary[500]} 50%, rgba(255, 255, 255, 0) 0%)`,
                backgroundPosition: 'bottom',
                backgroundRepeat: 'repeat-x',
                backgroundSize: '6px 1px',
              }}
            >
              <Grid>
                {role === 'care_navigator_nurse' ? <Nurse /> : <Pharmacist />}
              </Grid>
              <Typography
                style={{
                  fontSize: '14px',
                  lineHeight: '28px',
                  color: patientAccessTheme.palette.primary.main,
                  fontWeight: 500,
                  margin: '0px 0px 0px 0px',
                }}
              >
                {name.length > 6 ? getTextWithToolTip(name, 6) : name}
              </Typography>
            </Grid>
          }
        />
      </Grid>
    </Grid>
  );
};

const formatCareNavigators = (careNavigators: any[]) => {
  const getAvatarInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const displayedCareNavigators = careNavigators.slice(0, 3);
  const additionalCareNavigatorsCount =
    careNavigators.length > 3 ? careNavigators.length - 3 : 0;

  return (
    <Grid
      style={{
        display: 'flex',
      }}
    >
      {displayedCareNavigators.map((navigator, index) => (
        <Tooltip
          arrow
          style={{
            backgroundColor: patientAccessTheme.palette.grey[900],
            padding: '10px',
            fontSize: '13px',
            fontFamily: 'Inter',
            fontWeight: 400,
            lineHeight: '16px',
            // marginTop: '2px',
            // color: patientAccessTheme.palette.status.white,
            color: patientAccessTheme.palette.grey[900],
            // marginLeft: '50px',
            marginTop: '-8px',
            marginLeft: '-34px',
          }}
          title={navigator.email}
          placement="bottom-start"
        >
          <Avatar
            key={index}
            src={navigator.imgSrc}
            alt={navigator.name}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: patientAccessTheme.palette.grey[300],
              border: '1px solid ' + patientAccessTheme.palette.grey[100],
              fontSize: '14px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: patientAccessTheme.palette.text.lowemphasis,
              marginLeft: index === 0 ? '0px' : '-8px',
            }}
          >
            {!navigator.imgSrc && getAvatarInitial(navigator.name)}
          </Avatar>
        </Tooltip>
      ))}
      {additionalCareNavigatorsCount > 0 && (
        <Avatar
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            backgroundColor: patientAccessTheme.palette.grey[300],
            border: '1px solid ' + patientAccessTheme.palette.grey[100],
            fontSize: '14px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: patientAccessTheme.palette.text.lowemphasis,
            marginLeft: '-8px',
          }}
        >
          +{additionalCareNavigatorsCount}
        </Avatar>
      )}
    </Grid>
  );
};

export const formatGroupsTableData = (
  employerGroupList: any[],
  handleEmployeerGroupCardClick: any,
  handleMenuOnClick: any,
) => {
  const rowdata: any[] = [];
  employerGroupList?.forEach((item) => {
    const row: any = [];

    row.push(
      formatEmployerGroupNameForTable(
        item.title,
        item.id,
        handleEmployeerGroupCardClick,
        false,
        item,
      ),
    );
    row.push(
      item.careNavigators?.length === 0
        ? '-'
        : formatCareNavigators(item.careNavigators),
    );
    row.push(item.members);
    row.push(item.moi);
    row.push(item.cases);
    row.push(
      <MenuDots
        handleMenuItemClick={handleMenuOnClick}
        data={{
          group_id: item.id,
          group_name: item.title,
        }}
        menuItems={[{ label: 'Delete', icon: <Trash /> }]}
      />,
    );

    rowdata.push(row);
  });
  return rowdata;
};

export const formatUnassignedPatients = (
  patients: any[],
  handleEmployeeClick: any,
  handleCareNavClick: any,
  handleAssignModal: any,
  handleChangePriority: any,
  cnId: string,
  handleMenuOnClick: any,
  memberIdentification: boolean,
) => {
  const rowdata: any[] = [];
  patients?.forEach((item) => {
    const row: any = [];
    const patientName = !memberIdentification
      ? item.member_id || '-'
      : item.patient_created_by === 'system'
      ? decryptData(item.name)
      : item.name
      ? item.name
      : '-';
    row.push(
      formatEmployerGroupNameForTable(
        patientName,
        item.patient_id,
        handleEmployeeClick,
        false,
        item,
      ),
    );
    row.push(
      <TypableDropdown
        style={{
          border: '1px solid ' + getPriorityButton(item.priority).labelColor,
          background: getPriorityButton(item.priority).labelBackgroundColor,
          color: getPriorityButton(item.priority).labelColor,
        }}
        dropDownOptions={['High', 'Medium', 'Low']}
        dropDownValues={['High', 'Medium', 'Low']}
        value={item.priority}
        placeholder={item.priority}
        width="110px"
        height="30px"
        handleInputChange={() => {}}
        handleChange={(event) =>
          handleChangePriority(item.case_id, event.target.value)
        }
        hideTextField={true}
        {...getPriorityButton(item.priority)}
      />,
    );
    // row.push(
    //   item.condition?.length > 12
    //     ? getTextWithToolTip(item.condition, 12)
    //     : item.condition
    //     ? item.condition
    //     : '-',
    // );
    row.push(
      item.type_of_trigger.length > 20
        ? getTextWithToolTip(item.type_of_trigger, 20)
        : item.type_of_trigger,
    );
    row.push(
      item.cn_name
        ? formatCareNavigatorForTable(
            item.cn_name,
            item.cn_id,
            item.cn_role,
            handleCareNavClick,
          )
        : '-',
    );
    row.push(
      <Button
        label={
          item.cn_id === cnId
            ? 'Accept Case'
            : item.cn_name
            ? 'Replace'
            : 'Assign New'
        }
        variant="outlined"
        onClick={() =>
          handleAssignModal(
            item.cn_id === cnId
              ? 'Accept'
              : item.cn_name
              ? 'Replace'
              : 'Assign',
            item,
          )
        }
        style={{
          padding: '9px 16px',
          width: '110px',
          height: '33px',
          border: '1px solid #919DD0',
          borderRadius: '6px',
          textTransform: 'none',
          fontSize: '12px',
          lineHeight: '12px',
          fontWeight: 500,
          color: '#6C7DC0',
        }}
      />,
    );

    row.push(
      <MenuDots
        handleMenuItemClick={handleMenuOnClick}
        data={{
          case_id: item.case_id,
          case_status: item.case_status,
        }}
        menuItems={[{ label: 'Delete', icon: <Trash /> }]}
      />,
    );

    rowdata.push(row);
  });
  return rowdata;
};

export const formatActivePatients = (
  patients: any[],
  handleCaseClick: any,
  handleCareNavClick: any,
  handleMenuOnClick: any,
  memberIdentification: boolean,
) => {
  const rowdata: any[] = [];
  patients?.forEach((item) => {
    const row: any = [];

    row.push(
      formatEmployerGroupNameForTable(
        item.case_id,
        item.case_id,
        handleCaseClick,
        true,
        item,
      ),
    );

    const patientName = !memberIdentification
      ? item.member_id || '-'
      : item.patient_created_by === 'system'
      ? decryptData(item.name)
      : item.name
      ? item.name
      : '-';
    row.push(moment(item.created_at).format('MM/DD/YYYY'));
    row.push(
      patientName?.length > 15
        ? getTextWithToolTip(patientName, 15)
        : patientName,
    );
    // row.push(
    //   item.condition?.length > 8
    //     ? getTextWithToolTip(item.condition, 8)
    //     : item.condition
    //     ? item.condition
    //     : '-',
    // );
    row.push(
      item.type_of_trigger.length > 14
        ? getTextWithToolTip(item.type_of_trigger, 14)
        : item.type_of_trigger,
    );

    const currentDate = moment();

    row.push(
      item.last_engagement
        ? '< ' +
            currentDate.diff(moment(item.last_engagement), 'days') +
            ' days'
        : '-',
    );
    row.push(
      item.last_outreach
        ? '< ' + currentDate.diff(moment(item.last_outreach), 'days') + ' days'
        : '-',
    );
    row.push(
      formatCareNavigatorForTable(
        item.cn_name,
        item.cn_id,
        item.cn_role,
        handleCareNavClick,
      ),
    );

    row.push(
      <MenuDots
        handleMenuItemClick={handleMenuOnClick}
        data={{
          case_id: item.case_id,
          case_status: item.case_status,
        }}
        menuItems={[{ label: 'Delete', icon: <Trash /> }]}
      />,
    );

    rowdata.push(row);
  });
  return rowdata;
};

export const formatClosedPatients = (
  patients: any[],
  handleCaseClick: any,
  isCaseClosed: boolean,
  handleMenuOnClick: any,
  memberIdentification: boolean,
) => {
  const rowdata: any[] = [];
  patients?.forEach((item) => {
    const row: any = [];

    row.push(
      formatEmployerGroupNameForTable(
        item.case_id,
        item.case_id,
        handleCaseClick,
        true,
        item.survey,
        isCaseClosed,
      ),
    );
    row.push(moment(item.created_at).format('MM/DD/YYYY'));
    row.push(moment(item.closed_at).format('MM/DD/YYYY'));
    const patientName = !memberIdentification
      ? item.member_id || '-'
      : item.patient_created_by === 'system'
      ? decryptData(item.name)
      : item.name
      ? item.name
      : '-';
    row.push(
      patientName?.length > 12
        ? getTextWithToolTip(patientName, 12)
        : patientName,
    );
    row.push(
      item.survey?.case_type.length > 8
        ? getTextWithToolTip(item.survey?.case_type || '-', 8)
        : item.survey?.case_type || '-',
    );
    row.push(item.total_interactions);

    row.push(
      <Typography
        fontSize="14px"
        lineHeight="24px"
        fontType="normal"
        textColor={'#15948B'}
      >
        {item.survey?.saving
          ? numberToCurrencyFormatter(item.survey?.saving || 0, true, 2)
          : '-'}
      </Typography>,
    );
    row.push(
      item.survey?.rating ? (
        <StarRating
          value={item.survey?.rating}
          color={getRatingColor(item.survey?.rating)}
          precision={0.1}
          showRating={
            <Typography
              fontSize="12px"
              lineHeight="19px"
              fontType="normal"
              textColor={patientAccessTheme.palette.text.lowemphasis}
            >
              {Number(item.survey?.rating || 0).toFixed(1)}
            </Typography>
          }
          readOnly={true}
        />
      ) : (
        '-'
      ),
    );
    row.push(
      <MenuDots
        handleMenuItemClick={handleMenuOnClick}
        data={{
          case_id: item.case_id,
          case_status: item.case_status,
        }}
        menuItems={[{ label: 'Delete', icon: <Trash /> }]}
      />,
    );

    rowdata.push(row);
  });
  return rowdata;
};

export const formatPatients = (
  patients: any[],
  // handleAddToMOI: any,
  // group_name: string,
  handleCategoryClick: any,
  existing: boolean,
  handleMemberClick: any,
  setTabIndex: any,
  memberIdentification: boolean,
) => {
  const rowdata: any[] = [];
  patients?.forEach((item) => {
    const row: any = [];
    const patientName = !memberIdentification
      ? item.member_id || '-'
      : item.patient_created_by === 'system'
      ? decryptData(item.name)
      : item.name
      ? item.name
      : '-';
    row.push(
      existing
        ? formatMemberForTable(
            patientName,
            item.member_id,
            handleMemberClick,
            true,
          )
        : patientName?.length > 12
        ? getTextWithToolTip(patientName, 12)
        : patientName,
    );
    row.push(item.age);
    row.push(item.gender);
    row.push(
      item.condition?.length > 18
        ? getTextWithToolTip(item.condition, 18)
        : item.condition
        ? item.condition
        : '-',
    );
    row.push(item.enroll_status);
    row.push(
      item.moi ? (
        item.category?.length === 0 ? (
          '-'
        ) : (
          formatEmployerGroupNameForTable(
            item.category?.join(','),
            '',
            handleCategoryClick,
            true,
            item,
          )
        )
      ) : (
        <Button
          label={'Add To M.O.I'}
          variant="outlined"
          onClick={() => handleCategoryClick(item)}
          style={{
            padding: '9px 16px',
            width: '120px',
            height: '30px',
            border: '1px solid #919DD0',
            borderRadius: '6px',
            textTransform: 'none',
            fontSize: '12px',
            lineHeight: '12px',
            fontWeight: 500,
            color: '#6C7DC0',
          }}
        />
      ),
    );
    row.push(
      <Button
        label={'Start Case'}
        variant="outlined"
        onClick={() => {
          handleMemberClick(item.member_id);
          setTabIndex(4);
        }}
        style={{
          padding: '9px 16px',
          width: '120px',
          height: '30px',
          border: '1px solid #919DD0',
          borderRadius: '6px',
          textTransform: 'none',
          fontSize: '12px',
          lineHeight: '12px',
          fontWeight: 500,
          color: '#6C7DC0',
        }}
      />,
    );

    rowdata.push(row);
  });
  return rowdata;
};

export const getCareNavIcon = (role: string) => {
  switch (role) {
    case 'Nurse':
      return <Nurse />;
    case 'Pharmacist':
      return <Pharmacist />;
    case 'Customer Service':
      return <CustomerService width={'18px'} height={'18px'} />;
    default:
      <></>;
  }
  return <></>;
};

export const formatCareNavigatorCardData = (careNavigator: any) => {
  return {
    imageUrl: careNavigator.imageUrl,
    name: `${careNavigator.firstName} ${careNavigator.lastName}`,
    rating: careNavigator.rating,
    precision: 0.1,
    starColor: getRatingColor(careNavigator.rating),
    careNavRole: careNavigator.careNavRole,
    careNavIcon: getCareNavIcon(careNavigator.careNavRole),
    email: careNavigator.email,
    contact: careNavigator.contact,
    details: [
      {
        label: 'Experience',
        value: careNavigator.experience || '-',
        showTooltip: true,
      },
      {
        label: 'Active Cases',
        value: (
          <Typography
            fontSize="14px"
            lineHeight="22px"
            fontType="medium"
            textColor={patientAccessTheme.palette.status.success}
            style={{
              color: patientAccessTheme.palette.status.success,
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            {careNavigator.activeCases} Active
          </Typography>
        ),
      },
      {
        label: 'Closed Cases',
        value: (
          <Typography
            fontSize="14px"
            lineHeight="22px"
            fontType="medium"
            textColor={patientAccessTheme.palette.status.error}
          >
            {careNavigator.closedCases} Closed
          </Typography>
        ),
      },
      {
        label: 'List of Groups',
        value:
          careNavigator.groups?.length === 0
            ? '-'
            : careNavigator.groups?.join(','),
        showTooltip: true,
      },
      {
        label: 'Ph. No',
        value: careNavigator.contact || '-',
      },
      {
        label: 'Speciality',
        value: careNavigator.speciality || '-',
      },
    ],
  };
};

export const formatRatingCardData = (rating: any) => {
  return {
    imageUrl: '',
    name: rating.name || '-',
    careNavName: rating.careNavName,
    careNavRoleIcon:
      rating.careNavRole === 'care_navigator_pharmacist' ? (
        <Pharmacist />
      ) : rating.careNavRole === 'care_navigator_nurse' ? (
        <Nurse />
      ) : (
        <CustomerService width={'18px'} height={'18px'} />
      ),
    rating: rating.rating || null,
    precision: 0.1,
    starColor: getRatingColor(rating.rating),
    ratingText: getRatingText(rating.rating),
    review: rating.review || '-',
  };
};

export const formatCareNavigatorProfileDetails = (careNavigator: any) => {
  return [
    {
      label: 'Experience',
      value: careNavigator.experience || '-',
    },
    {
      label: 'No. Of Paitents',
      value: careNavigator.members || '-',
    },
    {
      label: 'List of Groups',
      value:
        careNavigator.groups?.length === 0
          ? '-'
          : careNavigator.groups
              ?.map((group: any) => group.groupName)
              .join(', '),
      showTooltip: true,
    },
    {
      label: 'Active/Closed Cases',
      value: (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Typography
            fontSize="14px"
            lineHeight="22px"
            fontType="medium"
            textColor={patientAccessTheme.palette.status.success}
          >
            {careNavigator.activeCases} Active
          </Typography>
          <Typography
            fontSize="14px"
            lineHeight="22px"
            fontType="medium"
            textColor={patientAccessTheme.palette.status.error}
          >
            {careNavigator.closedCases} Closed
          </Typography>
        </div>
      ),
    },
    {
      label: 'Speciality',
      value: careNavigator.speciality || '-',
    },
  ];
};

export const radioLegend = (
  color: string,
  value: string,
  cheked?: boolean,
  handleChange?: () => void,
) => {
  return (
    <Grid
      style={{
        display: 'flex',
        alignItems: 'center',
        margin: '0px 24px 0px 0px',
      }}
    >
      <RadioButton
        color={color}
        checked={cheked ?? false}
        handleChange={handleChange}
      />
      <Grid>
        <Typography
          fontSize="12px"
          lineHeight="24px"
          textColor={patientAccessTheme.palette.text.mediumemphasis}
        >
          {value}
        </Typography>
      </Grid>
    </Grid>
  );
};

export const formatCareNavigatorModalData = (
  careNavigator: any,
  allScreensCareNavigation: any,
) => {
  return {
    firstName: careNavigator.firstName,
    lastName: careNavigator.lastName,
    role:
      careNavigator.careNavRole === 'Pharmacist'
        ? 'care_navigator_pharmacist'
        : 'care_navigator_nurse',
    email: careNavigator.email,
    contact: careNavigator.contact,
    experience: careNavigator.experience,
    speciality: careNavigator.speciality,
    groups: careNavigator.groups?.map((group: any) => ({
      id: group.groupId,
      name: group.groupName,
    })),
    features: formatScreensArray(
      careNavigator.features,
      allScreensCareNavigation,
    ),
  };
};
